/**
 * Geo.js — the level team's geometry foundry.
 *
 * Everything here is authored in code, deterministic, and built to merge:
 *   - chamfered/bevelled primitives (no raw 90° edges anywhere in the map)
 *   - profile-extrusion helpers (arches, cornices, kerbs, mouldings, punched walls)
 *   - a Batcher that merges thousands of authored pieces into a handful of draw calls
 *   - an InstanceLib for repeated props
 *   - world-space planar UVs + baked vertex-colour dirt/AO
 *
 * owner: level
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { fbm2, clamp, smoothstep } from '../core/Rand.js';

const KEEP_ATTRS = ['position', 'normal', 'uv', 'color'];

/* ------------------------------------------------------------------ *
 * small maths helpers
 * ------------------------------------------------------------------ */

/** Compose a TRS matrix quickly. rot may be a number (Y radians) or [x,y,z]. */
export function xform(pos = [0, 0, 0], rot = 0, scale = 1) {
  const m = new THREE.Matrix4();
  const e = new THREE.Euler();
  if (typeof rot === 'number') e.set(0, rot, 0);
  else e.set(rot[0] || 0, rot[1] || 0, rot[2] || 0, 'YXZ');
  const q = new THREE.Quaternion().setFromEuler(e);
  const s = typeof scale === 'number' ? new THREE.Vector3(scale, scale, scale)
    : new THREE.Vector3(scale[0], scale[1], scale[2]);
  m.compose(new THREE.Vector3(pos[0], pos[1], pos[2]), q, s);
  return m;
}

const _a = new THREE.Vector3(), _b = new THREE.Vector3(), _d = new THREE.Vector3();
const _y = new THREE.Vector3(), _z = new THREE.Vector3(), _up = new THREE.Vector3(0, 1, 0);

/** Matrix that maps a centred local +X span onto the segment a→b (beams, rails, cables). */
export function alignX(a, b) {
  _a.set(a[0] ?? a.x, a[1] ?? a.y, a[2] ?? a.z);
  _b.set(b[0] ?? b.x, b[1] ?? b.y, b[2] ?? b.z);
  _d.subVectors(_b, _a);
  const len = _d.length() || 1e-5;
  _d.multiplyScalar(1 / len);
  _z.crossVectors(_d, _up);
  if (_z.lengthSq() < 1e-8) _z.set(0, 0, 1); else _z.normalize();
  _y.crossVectors(_z, _d).normalize();
  const m = new THREE.Matrix4().makeBasis(_d, _y, _z);
  m.setPosition((_a.x + _b.x) / 2, (_a.y + _b.y) / 2, (_a.z + _b.z) / 2);
  m.userLength = len;
  return m;
}
export function segLength(a, b) {
  return Math.hypot((b[0] ?? b.x) - (a[0] ?? a.x), (b[1] ?? b.y) - (a[1] ?? a.y), (b[2] ?? b.z) - (a[2] ?? a.z));
}

/** Catenary-ish sag curve between two points (hanging cable / laundry line). */
export function catenary(a, b, sag = 0.6, segs = 14) {
  const pts = [];
  for (let i = 0; i <= segs; i++) {
    const t = i / segs;
    const x = a[0] + (b[0] - a[0]) * t;
    const y = a[1] + (b[1] - a[1]) * t;
    const z = a[2] + (b[2] - a[2]) * t;
    // cosh-shaped droop normalised to 0 at the ends
    const d = (Math.cosh((t - 0.5) * 2.6) - Math.cosh(1.3)) / (1 - Math.cosh(1.3));
    pts.push(new THREE.Vector3(x, y - sag * d, z));
  }
  return pts;
}

/* ------------------------------------------------------------------ *
 * primitives
 * ------------------------------------------------------------------ */

/**
 * Chamfered box — 6 flat faces + 12 chamfer strips + 8 corner triangles.
 * The chamfers are the single biggest "this was made by a studio" tell: they
 * catch a specular highlight along every silhouette edge.
 */
export function chamferBox(w, h, d, bev = 0.035) {
  const hx = w / 2, hy = h / 2, hz = d / 2;
  const b = Math.max(0.0015, Math.min(bev, Math.min(hx, hy, hz) * 0.45));
  const P = [], N = [];
  const vp = (s, axis) => [
    s[0] * (axis === 0 ? hx : hx - b),
    s[1] * (axis === 1 ? hy : hy - b),
    s[2] * (axis === 2 ? hz : hz - b),
  ];
  const tri = (a, p, c, n) => {
    const e1 = [p[0] - a[0], p[1] - a[1], p[2] - a[2]];
    const e2 = [c[0] - a[0], c[1] - a[1], c[2] - a[2]];
    const cr = [e1[1] * e2[2] - e1[2] * e2[1], e1[2] * e2[0] - e1[0] * e2[2], e1[0] * e2[1] - e1[1] * e2[0]];
    if (cr[0] * n[0] + cr[1] * n[1] + cr[2] * n[2] < 0) { const t = p; p = c; c = t; }
    P.push(a[0], a[1], a[2], p[0], p[1], p[2], c[0], c[1], c[2]);
    for (let i = 0; i < 3; i++) N.push(n[0], n[1], n[2]);
  };
  const quad = (a, p, c, e, n) => { tri(a, p, c, n); tri(a, c, e, n); };
  const SG = [-1, 1];

  for (let axis = 0; axis < 3; axis++) {
    for (const s of SG) {
      const n = [0, 0, 0]; n[axis] = s;
      const a1 = (axis + 1) % 3, a2 = (axis + 2) % 3;
      const c = [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([u, v]) => {
        const sg = [0, 0, 0]; sg[axis] = s; sg[a1] = u; sg[a2] = v;
        return vp(sg, axis);
      });
      quad(c[0], c[1], c[2], c[3], n);
    }
  }
  const inv = 1 / Math.SQRT2;
  for (let i = 0; i < 3; i++) {
    for (let j = i + 1; j < 3; j++) {
      const k = 3 - i - j;
      for (const si of SG) {
        for (const sj of SG) {
          const n = [0, 0, 0]; n[i] = si * inv; n[j] = sj * inv;
          const pts = [];
          for (const sk of SG) {
            const sg = [0, 0, 0]; sg[i] = si; sg[j] = sj; sg[k] = sk;
            pts.push(vp(sg, i), vp(sg, j));
          }
          quad(pts[0], pts[1], pts[3], pts[2], n);
        }
      }
    }
  }
  const l3 = 1 / Math.sqrt(3);
  for (const sx of SG) for (const sy of SG) for (const sz of SG) {
    const sg = [sx, sy, sz];
    tri(vp(sg, 0), vp(sg, 1), vp(sg, 2), [sx * l3, sy * l3, sz * l3]);
  }

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(P, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(N, 3));
  planarUV(g, 1);
  return g;
}

/** Plain box — collision proxies only, never rendered. */
export function plainBox(w, h, d) {
  return new THREE.BoxGeometry(w, h, d).toNonIndexed();
}

/** Cylinder with chamfered top/bottom rims. */
export function chamferCyl(r, h, seg = 14, bev = 0.03, openEnded = false) {
  const b = Math.min(bev, Math.min(r, h / 2) * 0.4);
  const parts = [];
  const side = new THREE.CylinderGeometry(r, r, h - 2 * b, seg, 1, true);
  parts.push(side);
  for (const s of [1, -1]) {
    const rim = new THREE.CylinderGeometry(r - b, r, b, seg, 1, true);
    if (s < 0) rim.rotateX(Math.PI);
    rim.translate(0, s * (h / 2 - b / 2), 0);
    parts.push(rim);
    if (!openEnded) {
      const cap = new THREE.CircleGeometry(r - b, seg);
      cap.rotateX(s > 0 ? -Math.PI / 2 : Math.PI / 2);
      cap.translate(0, s * (h / 2), 0);
      parts.push(cap);
    }
  }
  const g = mergeGeometries(parts.map((p) => stripAttrs(p.toNonIndexed())), false);
  parts.forEach((p) => p.dispose());
  planarUV(g, 1);
  return g;
}

/** Tapered "cone-ish" tube, chamfer-free but smooth — jerrycan spouts, tree trunks. */
export function taperCyl(r0, r1, h, seg = 10) {
  const g = new THREE.CylinderGeometry(r1, r0, h, seg, 1, false).toNonIndexed();
  return stripAttrs(g);
}

/** Extrude a THREE.Shape with a real bevel on both ends, centred on Z. */
export function extrudeShape(shape, depth, bev = 0.025, curveSegments = 10) {
  const b = Math.max(0.001, Math.min(bev, depth * 0.35));
  const d = Math.max(0.002, depth - 2 * b);
  const g = new THREE.ExtrudeGeometry(shape, {
    depth: d, bevelEnabled: true, bevelSize: b, bevelThickness: b,
    bevelSegments: 1, curveSegments, steps: 1,
  });
  g.translate(0, 0, -d / 2);
  return stripAttrs(g); // ExtrudeGeometry is already non-indexed
}

/** Rectangle path helper. */
function rectPath(x0, y0, x1, y1) {
  const p = new THREE.Path();
  p.moveTo(x0, y0); p.lineTo(x1, y0); p.lineTo(x1, y1); p.lineTo(x0, y1); p.closePath();
  return p;
}

/**
 * A punched wall panel: length along local X (centred), height along local Y
 * (0 = base), thickness along local Z (centred). Openings are cut as holes so
 * every reveal gets a bevel for free.
 *   openings: [{ x, y, w, h, arch }]  (x centre, y sill)
 */
export function wallPanel({ length, height, thickness = 0.35, openings = [], bevel = 0.03 }) {
  const shape = new THREE.Shape();
  const hl = length / 2;
  shape.moveTo(-hl, 0); shape.lineTo(hl, 0); shape.lineTo(hl, height); shape.lineTo(-hl, height); shape.closePath();
  for (const o of openings) {
    const x0 = o.x - o.w / 2, x1 = o.x + o.w / 2;
    const y0 = o.y, y1 = o.y + o.h;
    const p = new THREE.Path();
    if (o.arch) {
      const r = o.w / 2;
      const springer = y1 - r;
      p.moveTo(x0, y0); p.lineTo(x0, springer);
      p.absarc(o.x, springer, r, Math.PI, 0, true);
      p.lineTo(x1, y0); p.closePath();
    } else {
      p.moveTo(x0, y0); p.lineTo(x0, y1); p.lineTo(x1, y1); p.lineTo(x1, y0); p.closePath();
    }
    shape.holes.push(p);
  }
  const g = extrudeShape(shape, thickness, bevel, 12);
  return g;
}

/** Free-standing arch (two piers + arched head) as a single bevelled extrusion. */
export function archwayPanel({ span, pierHeight, thickness = 0.5, jamb = 0.55, bevel = 0.04, arch = true }) {
  const r = span / 2;
  const outerW = span + jamb * 2;
  const total = pierHeight + (arch ? r : 0) + 0.35;
  const shape = new THREE.Shape();
  shape.moveTo(-outerW / 2, 0); shape.lineTo(outerW / 2, 0);
  shape.lineTo(outerW / 2, total); shape.lineTo(-outerW / 2, total); shape.closePath();
  const p = new THREE.Path();
  p.moveTo(-r, 0); p.lineTo(-r, pierHeight);
  if (arch) p.absarc(0, pierHeight, r, Math.PI, 0, true);
  else p.lineTo(r, pierHeight);
  p.lineTo(r, 0); p.closePath();
  shape.holes.push(p);
  return extrudeShape(shape, thickness, bevel, 14);
}

/** Kerb / chamfered street edge. Runs along local X, profile in Y/Z. */
export function kerbProfile(length, w = 0.28, h = 0.16, cham = 0.05) {
  const s = new THREE.Shape();
  s.moveTo(-w / 2, 0); s.lineTo(w / 2, 0);
  s.lineTo(w / 2, h - cham); s.lineTo(w / 2 - cham, h);
  s.lineTo(-w / 2 + cham, h); s.lineTo(-w / 2, h - cham);
  s.closePath();
  const g = extrudeShape(s, length, 0.02, 4);
  g.rotateY(Math.PI / 2);
  return g;
}

/** Cornice / string-course moulding. Profile in local X (out) / Y (up), runs along Z. */
export function corniceProfile(length, proj = 0.22, h = 0.3) {
  const s = new THREE.Shape();
  s.moveTo(0, 0);
  s.lineTo(proj * 0.45, 0.02);
  s.lineTo(proj, h * 0.42);
  s.lineTo(proj, h * 0.78);
  s.lineTo(proj * 0.72, h);
  s.lineTo(0, h);
  s.closePath();
  const g = extrudeShape(s, length, 0.02, 4);
  return g; // extruded along Z, profile in XY
}

/** A flight of stairs with chamfered nosings. Steps ascend +Z, width along X. */
export function stairFlight({ steps = 10, rise = 0.18, run = 0.29, width = 1.6, bevel = 0.02 }) {
  const parts = [];
  for (let i = 0; i < steps; i++) {
    const h = rise * (i + 1);
    const t = chamferBox(width, h, run, bevel);
    t.translate(0, h / 2, run * (i + 0.5));
    parts.push(t);
  }
  const g = mergeGeometries(parts, false);
  parts.forEach((p) => p.dispose());
  return g;
}

/** Simple pipe/tube along a point list. */
export function tube(points, r = 0.04, radial = 6) {
  const curve = new THREE.CatmullRomCurve3(points.map((p) => (p.isVector3 ? p : new THREE.Vector3(p[0], p[1], p[2]))));
  const g = new THREE.TubeGeometry(curve, Math.max(6, points.length * 2), r, radial, false);
  return stripAttrs(g.toNonIndexed());
}

/**
 * Append a mirrored copy of every triangle so a card is lit correctly from both
 * sides. Cheaper and far more reliable than depending on material.side, which we
 * do not own.
 */
export function twoSided(geo) {
  const g = stripAttrs(geo);
  const src = { p: g.attributes.position.array, n: g.attributes.normal.array, u: g.attributes.uv.array };
  const n = g.attributes.position.count;
  const P = new Float32Array(n * 6), N = new Float32Array(n * 6), U = new Float32Array(n * 4);
  P.set(src.p, 0); N.set(src.n, 0); U.set(src.u, 0);
  for (let t = 0; t < n; t += 3) {
    for (let k = 0; k < 3; k++) {
      const from = (t + (2 - k)) * 3, to = (n + t + k) * 3;
      P[to] = src.p[from]; P[to + 1] = src.p[from + 1]; P[to + 2] = src.p[from + 2];
      N[to] = -src.n[from]; N[to + 1] = -src.n[from + 1]; N[to + 2] = -src.n[from + 2];
      U[(n + t + k) * 2] = src.u[(t + (2 - k)) * 2];
      U[(n + t + k) * 2 + 1] = src.u[(t + (2 - k)) * 2 + 1];
    }
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(P, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(N, 3));
  out.setAttribute('uv', new THREE.BufferAttribute(U, 2));
  return out;
}

/** Flat quad in XZ (ground patches) or XY (cards). */
export function quadXZ(w, d, segs = 1, both = false) {
  const g = new THREE.PlaneGeometry(w, d, segs, segs);
  g.rotateX(-Math.PI / 2);
  const out = stripAttrs(g.toNonIndexed());
  return both ? twoSided(out) : out;
}
export function quadXY(w, h, both = false) {
  const out = stripAttrs(new THREE.PlaneGeometry(w, h).toNonIndexed());
  return both ? twoSided(out) : out;
}

/** Tapered leaf / frond blade, built as real geometry so it needs no alpha map. */
export function blade(len, wide, curve = 0.35, segs = 5, twist = 0) {
  const P = [], N = [], UV = [];
  const pt = (t, s) => {
    const w = wide * Math.sin(Math.PI * Math.min(1, t * 0.92 + 0.08)) * (1 - t * 0.25);
    const y = len * t - curve * len * t * t;
    const z = Math.sin(t * Math.PI) * curve * len * 0.28;
    const a = twist * t;
    return [s * w * Math.cos(a), y, z + s * w * Math.sin(a) * 0.4];
  };
  for (let i = 0; i < segs; i++) {
    const t0 = i / segs, t1 = (i + 1) / segs;
    const a = pt(t0, -1), b = pt(t0, 1), c = pt(t1, 1), d = pt(t1, -1);
    const uvs = { a: [0, t0], b: [1, t0], c: [1, t1], d: [0, t1] };
    for (const tri of [['a', 'b', 'c'], ['a', 'c', 'd']]) {
      for (const key of tri) {
        const p = { a, b, c, d }[key];
        P.push(p[0], p[1], p[2]);
        N.push(0, 0.35, 0.94);
        UV.push(uvs[key][0], uvs[key][1]);
      }
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(P, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(N, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(UV, 2));
  g.computeVertexNormals();
  return twoSided(g); // leaves must read from both sides
}

/* ------------------------------------------------------------------ *
 * attribute plumbing
 * ------------------------------------------------------------------ */

/** Drop everything the merger can choke on; guarantee position/normal/uv. */
export function stripAttrs(geo) {
  const g = geo.index ? geo.toNonIndexed() : geo;
  for (const k of Object.keys(g.attributes)) {
    if (!KEEP_ATTRS.includes(k)) g.deleteAttribute(k);
  }
  if (!g.attributes.normal) g.computeVertexNormals();
  if (!g.attributes.uv) planarUV(g, 1);
  g.clearGroups();
  g.morphAttributes = {};
  return g;
}

/** Per-triangle planar projection on the dominant normal axis. Seamless on merges. */
export function planarUV(geo, scale = 1, offset = [0, 0]) {
  const pos = geo.attributes.position;
  const n = pos.count;
  const uv = new Float32Array(n * 2);
  const s = 1 / Math.max(1e-5, scale);
  for (let i = 0; i < n; i += 3) {
    const ax = pos.getX(i), ay = pos.getY(i), az = pos.getZ(i);
    const bx = pos.getX(i + 1), by = pos.getY(i + 1), bz = pos.getZ(i + 1);
    const cx = pos.getX(i + 2), cy = pos.getY(i + 2), cz = pos.getZ(i + 2);
    const e1x = bx - ax, e1y = by - ay, e1z = bz - az;
    const e2x = cx - ax, e2y = cy - ay, e2z = cz - az;
    const nx = Math.abs(e1y * e2z - e1z * e2y);
    const ny = Math.abs(e1z * e2x - e1x * e2z);
    const nz = Math.abs(e1x * e2y - e1y * e2x);
    let u0, u1;
    if (nx >= ny && nx >= nz) { u0 = 2; u1 = 1; }
    else if (ny >= nz) { u0 = 0; u1 = 2; }
    else { u0 = 0; u1 = 1; }
    for (let k = 0; k < 3; k++) {
      const j = i + k;
      const p = [pos.getX(j), pos.getY(j), pos.getZ(j)];
      uv[j * 2] = p[u0] * s + offset[0];
      uv[j * 2 + 1] = p[u1] * s + offset[1];
    }
  }
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  return geo;
}

/** Ensure a vertex-colour attribute, optionally initialised to a tint. */
export function ensureColor(geo, tint) {
  const n = geo.attributes.position.count;
  if (!geo.attributes.color || geo.attributes.color.count !== n) {
    const arr = new Float32Array(n * 3).fill(1);
    geo.setAttribute('color', new THREE.BufferAttribute(arr, 3));
  }
  if (tint) {
    const c = geo.attributes.color;
    for (let i = 0; i < n; i++) {
      c.setXYZ(i, c.getX(i) * tint[0], c.getY(i) * tint[1], c.getZ(i) * tint[2]);
    }
  }
  return geo;
}

/* ------------------------------------------------------------------ *
 * Batcher — merges authored pieces into one mesh per material/surface
 * ------------------------------------------------------------------ */

/**
 * How hard the vertex-colour bake presses on a given material.
 * `underside` is added to the occlusion term on down-facing vertices; a
 * negative value *lifts* them, which is what a cloth canopy or a leaf card
 * actually does — light comes through, so the underside must not go to black.
 */
const DEFAULT_AO = { prox: 0.34, proxTight: 0.28, contact: 0.36, underside: 0.18, min: 0.24, max: 1.14, cmax: 1.2, macro: 0.11 };
/** Materials that are mostly ground: they get the hard macro break-up. */
const GROUND_AO = { ...DEFAULT_AO, macro: 0.19, min: 0.23, max: 1.18 };
const AO_PROFILES = {
  // the ground tile is findable by eye at ~300 px, which CRITIC.md §2 makes an
  // instant fail. A strong aperiodic vertex-colour field is half the cure (the
  // other half is the overlaid incommensurate UV pitches in Street.js).
  sand: GROUND_AO, gravel: GROUND_AO, dirt: GROUND_AO,
  asphalt: GROUND_AO, concrete_floor: GROUND_AO,
  /*
   * Souk canopies, awnings, hanging rugs. `canvas` has a linear albedo of
   * 0.05–0.14 — plausible for raw duck, far too dark for the sun-bleached,
   * dyed market cloth this map is dressed with. `cmax` is the ceiling on the
   * final vertex colour, so raising it is what lets a tint above 1.0 through
   * instead of being clipped back to the material's own gloom. The negative
   * `underside` is baked translucency: real cloth transmits, and a bake that
   * paints the underside near-black is simply wrong.
   */
  canvas: { prox: 0.10, proxTight: 0.06, contact: 0.10, underside: -0.28, min: 0.66, max: 1.34, cmax: 2.8 },
  fabric_tarp: { prox: 0.14, proxTight: 0.10, contact: 0.16, underside: -0.16, min: 0.58, max: 1.24, cmax: 2.4 },
  sandbag: { prox: 0.26, proxTight: 0.22, contact: 0.30, underside: 0.12, min: 0.34, max: 1.14, cmax: 1.4 },
  // leaf cards: two-sided geometry, so half the triangles face down by design
  foliage: { prox: 0.20, proxTight: 0.14, contact: 0.20, underside: -0.06, min: 0.40, max: 1.18, cmax: 1.5 },
  // glass must not pick up a dirt gradient that reads as a smear
  glass: { prox: 0.04, proxTight: 0.02, contact: 0.04, underside: 0, min: 0.82, max: 1.10, cmax: 1.2 },
  glass_dirty: { prox: 0.12, proxTight: 0.08, contact: 0.10, underside: 0.04, min: 0.68, max: 1.10, cmax: 1.2 },
};

export class Batcher {
  constructor(rng) {
    this.rng = rng;
    this.groups = new Map();
    this.occluders = [];   // {x0,z0,x1,z1,h} footprints used to bake contact dirt
    this.stats = { pieces: 0, verts: 0 };
  }

  /** Register a footprint that should darken nearby ground/wall vertices. */
  occluder(x0, z0, x1, z1, h = 3) {
    this.occluders.push({
      x0: Math.min(x0, x1), x1: Math.max(x0, x1),
      z0: Math.min(z0, z1), z1: Math.max(z0, z1), h,
    });
  }

  /**
   * @param {THREE.BufferGeometry} geo  consumed (cloned internally, safe to reuse)
   * @param {string} mat  material name from the guaranteed list
   * @param {object} opts { matrix, surface, uvScale, uvOffset, tint, cast, recv, ao }
   */
  add(geo, mat, opts = {}) {
    const g = stripAttrs(geo.clone());
    if (opts.matrix) g.applyMatrix4(opts.matrix);
    if (opts.uv !== 'keep') planarUV(g, opts.uvScale ?? 1.6, opts.uvOffset);
    ensureColor(g, opts.tint);
    if (opts.ao !== false) g.userData.ao = true;
    const surface = opts.surface ?? 'concrete';
    const cast = opts.cast !== false;
    const recv = opts.recv !== false;
    const key = `${mat}|${surface}|${cast ? 1 : 0}${recv ? 1 : 0}`;
    let e = this.groups.get(key);
    if (!e) { e = { mat, surface, cast, recv, list: [] }; this.groups.set(key, e); }
    e.list.push(g);
    this.stats.pieces++;
    this.stats.verts += g.attributes.position.count;
    return this;
  }

  /** Convenience: chamfered box straight into the batch. */
  box(w, h, d, mat, opts = {}) {
    const g = chamferBox(w, h, d, opts.bevel ?? 0.035);
    this.add(g, mat, opts);
    g.dispose();
    return this;
  }

  /* -------- baked dirt / AO ------------------------------------------ */

  _buildDF() {
    const MIN = -70, MAX = 70, CELL = 0.4;
    const W = Math.ceil((MAX - MIN) / CELL);
    const INF = 1e6;
    const d = new Float32Array(W * W).fill(INF);
    for (const o of this.occluders) {
      const i0 = Math.max(0, Math.floor((o.x0 - MIN) / CELL));
      const i1 = Math.min(W - 1, Math.ceil((o.x1 - MIN) / CELL));
      const j0 = Math.max(0, Math.floor((o.z0 - MIN) / CELL));
      const j1 = Math.min(W - 1, Math.ceil((o.z1 - MIN) / CELL));
      for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) d[j * W + i] = 0;
    }
    const R2 = Math.SQRT2;
    for (let j = 0; j < W; j++) {
      for (let i = 0; i < W; i++) {
        const k = j * W + i;
        let v = d[k];
        if (i > 0) v = Math.min(v, d[k - 1] + 1);
        if (j > 0) v = Math.min(v, d[k - W] + 1);
        if (i > 0 && j > 0) v = Math.min(v, d[k - W - 1] + R2);
        if (i < W - 1 && j > 0) v = Math.min(v, d[k - W + 1] + R2);
        d[k] = v;
      }
    }
    for (let j = W - 1; j >= 0; j--) {
      for (let i = W - 1; i >= 0; i--) {
        const k = j * W + i;
        let v = d[k];
        if (i < W - 1) v = Math.min(v, d[k + 1] + 1);
        if (j < W - 1) v = Math.min(v, d[k + W] + 1);
        if (i < W - 1 && j < W - 1) v = Math.min(v, d[k + W + 1] + R2);
        if (i > 0 && j < W - 1) v = Math.min(v, d[k + W - 1] + R2);
        d[k] = v;
      }
    }
    return {
      sample(x, z) {
        const fi = clamp((x - MIN) / CELL, 0, W - 1.001);
        const fj = clamp((z - MIN) / CELL, 0, W - 1.001);
        const i = fi | 0, j = fj | 0, tx = fi - i, tz = fj - j;
        const a = d[j * W + i], b = d[j * W + i + 1];
        const c = d[(j + 1) * W + i], e = d[(j + 1) * W + i + 1];
        return (a * (1 - tx) + b * tx) * (1 - tz) + (c * (1 - tx) + e * tx) * tz;
      },
      CELL,
    };
  }

  /** Bake contact dirt, ground occlusion, sun-bleach gradient and macro variation. */
  _bakeAO(geo, df, prof = DEFAULT_AO) {
    const pos = geo.attributes.position;
    const nrm = geo.attributes.normal;
    const col = geo.attributes.color;
    const n = pos.count;
    for (let i = 0; i < n; i++) {
      const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
      const dist = df.sample(x, z) * df.CELL;
      // proximity to a wall footprint, only meaningful near the ground
      const prox = 1 - smoothstep(0.05, 2.0, dist);
      const proxTight = 1 - smoothstep(0.02, 0.55, dist);
      const hFall = 1 - smoothstep(0.1, 3.6, y);
      /*
       * Contact grime is where a surface MEETS something, so it has to be
       * gated on proximity to an occluder. Ungated, this term subtracted 36 %
       * from every vertex within half a metre of y = 0 — which is every square
       * metre of paving, street and interior floor in the map — and that flat
       * darkening is a large part of why the ground read as black in
       * `level_interior` and `level_detail`. Near a wall it is unchanged; in
       * the open it now does nothing, which is correct.
       */
      const near = clamp(prox * 0.8 + proxTight * 0.55, 0, 1);
      const contact = (1 - smoothstep(0.0, 0.55, Math.max(0, y))) * near;
      let occ = clamp(prox * hFall * prof.prox + proxTight * hFall * prof.proxTight + contact * prof.contact, 0, 0.68);
      // undersides read as shadowed — except on thin translucent sheets (cloth,
      // leaf cards), where the real material transmits light and a bake that
      // paints the underside near-black is simply wrong.
      if (nrm.getY(i) < -0.35) occ = clamp(occ + prof.underside, -0.14, 0.75);
      /*
       * Macro grime/bleach variation.
       *
       * Two changes, both aimed at the "readable ~300 px ground tile".
       *
       * 1. The second octave used to be `fbm2(x * 0.21, y * 0.18)`. On a ground
       *    plane y is constant, so that term collapsed to a function of x alone
       *    — a 1-D ripple running along the world X axis across every floor in
       *    the map. Up-facing geometry now samples (x, z), which is the plane it
       *    actually lies in, and gets a third finer octave as well.
       * 2. `prof.macro` lets ground materials press ~3x harder. A vertex-colour
       *    field at 18 m / 5 m / 1.6 m wavelengths is aperiodic against the
       *    texture's own repeat, so it desynchronises the lattice at exactly the
       *    scales the eye uses to find it. It is mean-zero, so it costs no
       *    overall brightness.
       */
      const flat = nrm.getY(i) > 0.72;
      const macro = flat
        ? fbm2(x * 0.055, z * 0.055, 3) * 0.5
          + fbm2(x * 0.205, z * 0.205, 2) * 0.30
          + fbm2(x * 0.62, z * 0.62, 2) * 0.17
        : fbm2(x * 0.055, z * 0.055, 3) * 0.5 + fbm2(x * 0.21, y * 0.18, 2) * 0.28;
      const v = clamp(1 - occ + macro * (prof.macro ?? 0.11), prof.min, prof.max);
      // dirt at the base is warmer and less blue; sun-bleached tops are cooler-bright
      const warm = occ;
      const cm = prof.cmax;
      col.setXYZ(i,
        clamp(col.getX(i) * v * (1 + warm * 0.05), 0, cm),
        clamp(col.getY(i) * v * (1 - warm * 0.02), 0, cm),
        clamp(col.getZ(i) * v * (1 - warm * 0.13), 0, cm));
    }
    col.needsUpdate = true;
    return geo;
  }

  /** Merge everything and attach to root. Returns the created meshes. */
  build(root, resolveMat, name = 'batch') {
    const df = this._buildDF();
    const meshes = [];
    for (const [key, e] of this.groups) {
      if (!e.list.length) continue;
      let merged;
      try {
        merged = mergeGeometries(e.list, false);
      } catch (err) {
        console.warn('[level] merge failed for', key, err);
        continue;
      }
      if (!merged) continue;
      this._bakeAO(merged, df, AO_PROFILES[e.mat] || DEFAULT_AO);
      merged.computeBoundingSphere();
      merged.computeBoundingBox();
      const mesh = new THREE.Mesh(merged, resolveMat(e.mat));
      mesh.name = `${name}:${e.mat}`;
      mesh.userData.surface = e.surface;
      mesh.castShadow = e.cast;
      mesh.receiveShadow = e.recv;
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      root.add(mesh);
      meshes.push(mesh);
      for (const g of e.list) g.dispose();
      e.list.length = 0;
    }
    return meshes;
  }
}

/* ------------------------------------------------------------------ *
 * InstanceLib — repeated props, one InstancedMesh per (kind, material)
 * ------------------------------------------------------------------ */

export class InstanceLib {
  constructor() {
    this.defs = new Map();     // kind -> { parts: Map<matKey,{mat,surface,geos:[]}> }
    this.placements = new Map(); // kind -> Matrix4[]
    this.tints = new Map();      // kind -> ([r,g,b] | null)[]  parallel to placements
    this.flags = new Map();      // kind -> { cast, recv }
  }

  /**
   * parts: [{ geo, mat, surface, matrix }] — geo is consumed by reference.
   * opts.cast/opts.recv control the shadow flags for every mesh of this kind.
   * Shadow cost is per *draw call*, and a cascade re-draws every caster, so a
   * prop only earns `cast: true` if its shadow is legible from standing height.
   */
  define(kind, parts, opts = {}) {
    this.flags.set(kind, { cast: opts.cast !== false, recv: opts.recv !== false });
    const map = new Map();
    for (const p of parts) {
      const key = `${p.mat}|${p.surface ?? 'concrete'}`;
      let e = map.get(key);
      if (!e) { e = { mat: p.mat, surface: p.surface ?? 'concrete', geos: [] }; map.set(key, e); }
      const g = stripAttrs(p.geo.clone());
      if (p.matrix) g.applyMatrix4(p.matrix);
      if (p.uv !== 'keep') planarUV(g, p.uvScale ?? 1.2, p.uvOffset);
      ensureColor(g, p.tint);
      if (p.ao) shadeByHeight(g, p.ao);
      e.geos.push(g);
    }
    this.defs.set(kind, map);
    this.placements.set(kind, []);
    this.tints.set(kind, []);
    return this;
  }

  /**
   * @param {string} kind
   * @param {THREE.Matrix4} matrix
   * @param {number[]|null} tint  per-INSTANCE rgb multiplier, uploaded as
   *   `instanceColor`. Three multiplies it into `vColor`, which the batch already
   *   uses, so a whole scatter of one prop kind can carry per-piece colour
   *   variation for **zero extra draw calls** — the single cheapest cure for
   *   "the same crate six times with the same texture phase".
   */
  place(kind, matrix, tint = null) {
    const al = this.aliases && this.aliases.get(kind);
    if (al) {
      const m = matrix.clone().scale(new THREE.Vector3(al.scale, al.scale, al.scale));
      return this.place(al.target, m, tint || al.tint || null);
    }
    const arr = this.placements.get(kind);
    if (!arr) { console.warn('[level] unknown prop kind', kind); return this; }
    arr.push(matrix);
    this.tints.get(kind).push(tint);
    return this;
  }

  /**
   * Point one prop name at another kind's InstancedMesh, with a scale.
   *
   * A prop kind costs one draw call per material *whatever its instance count*,
   * so "the big crate" and "the small crate" being separate kinds bought two
   * draws for one silhouette. Aliasing keeps the call sites and the size
   * difference and gives the draw call back.
   */
  alias(kind, target, scale = 1, tint = null) {
    if (!this.aliases) this.aliases = new Map();
    this.aliases.set(kind, { target, scale, tint });
    return this;
  }

  count(kind) {
    const al = this.aliases && this.aliases.get(kind);
    if (al) return this.count(al.target);
    return this.placements.get(kind)?.length ?? 0;
  }

  build(root, resolveMat) {
    const meshes = [];
    const _c = new THREE.Color();
    for (const [kind, map] of this.defs) {
      const mats = this.placements.get(kind);
      if (!mats || !mats.length) continue;
      const tints = this.tints.get(kind) || [];
      const anyTint = tints.some((t) => t);
      for (const [, e] of map) {
        if (!e.geos.length) continue;
        let geo;
        try { geo = e.geos.length === 1 ? e.geos[0] : mergeGeometries(e.geos, false); }
        catch (err) { console.warn('[level] instance merge failed', kind, err); continue; }
        if (!geo) continue;
        geo.computeBoundingSphere();
        const im = new THREE.InstancedMesh(geo, resolveMat(e.mat), mats.length);
        im.name = `prop:${kind}:${e.mat}`;
        for (let i = 0; i < mats.length; i++) im.setMatrixAt(i, mats[i]);
        im.instanceMatrix.needsUpdate = true;
        if (anyTint) {
          for (let i = 0; i < mats.length; i++) {
            const t = tints[i];
            _c.setRGB(t ? t[0] : 1, t ? t[1] : 1, t ? t[2] : 1);
            im.setColorAt(i, _c);
          }
          if (im.instanceColor) im.instanceColor.needsUpdate = true;
        }
        const fl = this.flags.get(kind) || { cast: true, recv: true };
        im.castShadow = fl.cast;
        im.receiveShadow = fl.recv;
        im.userData.surface = e.surface;
        im.frustumCulled = true;
        im.computeBoundingSphere?.();
        root.add(im);
        meshes.push(im);
      }
    }
    return meshes;
  }
}

/** Cheap local-space vertical gradient for props (grounds them without the DF). */
export function shadeByHeight(geo, opts = {}) {
  const { from = 0, to = 0.6, dark = 0.55 } = typeof opts === 'object' ? opts : {};
  const pos = geo.attributes.position;
  ensureColor(geo);
  const col = geo.attributes.color;
  for (let i = 0; i < pos.count; i++) {
    const t = smoothstep(from, to, pos.getY(i));
    const v = dark + (1 - dark) * t;
    col.setXYZ(i, col.getX(i) * v, col.getY(i) * v, col.getZ(i) * v * 0.97);
  }
  col.needsUpdate = true;
  return geo;
}

export { mergeGeometries };
